import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CostContext, { formatCentsToDollars } from '../contexts/CostContext';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const ScanPaymentModal = ({
  open,
  onClose,
  onConfirm,
  cardBrand,
  cardLast4,
  isFreeScan,
  isFollowUp,
  isMultiple,
  isPractitionerFamily,
  practitionerId,
  outgoingTracking,
  scanId,
}) => {
  const [isFirstInSeries, setIsFirstInSeries] = useState(true);

  useEffect(() => {
    const checkHasPaidSiblingScan = async () => {
      if (isPractitionerFamily && isMultiple && !isFreeScan && practitionerId && outgoingTracking && scanId) {
        try {
          const practitionerActiveScansRef = doc(db, 'activeScans', practitionerId);
          const practitionerActiveScansSnap = await getDoc(practitionerActiveScansRef);
          if (practitionerActiveScansSnap.exists()) {
            const practitionerActiveScansData = practitionerActiveScansSnap.data();
            const hasPaidSiblingScan = practitionerActiveScansData.scans.some(s => {
              return (
                s.id !== scanId &&
                s.scanStatus >= 4 &&
                s.outgoingTracking === outgoingTracking
              );
            });
            setIsFirstInSeries(!hasPaidSiblingScan);
          }
        } catch (err) {
          console.error('Error checking for paid sibling scans:', err);
          // Default to true (first in series) on error to avoid undercharging
          setIsFirstInSeries(true);
        }
      }
    };

    checkHasPaidSiblingScan();
  }, [isPractitionerFamily, isMultiple, isFreeScan, practitionerId, outgoingTracking, scanId]);

  const getTitle = () => {
    if (isFreeScan) return 'Complimentary Practitioner Scan';
    if (isPractitionerFamily && isMultiple) return isFirstInSeries ? 'Practitioner Family Rate' : 'Practitioner Family Multi-Scan Rate';
    if (isPractitionerFamily) return 'Practitioner Family Rate';
    if (isMultiple) return 'Multiple Scans (Same Household)';
    if (isFollowUp) return 'Follow-up Scan';
    return 'Initial Scan';
  };

  const getAmount = () => {
    if (isFreeScan) return 0;

    if (isPractitionerFamily) {
      if (isMultiple) {
        return isFirstInSeries ? CostContext.ScanBalanceFamily : CostContext.ScanBalanceFamilyMultiple;
      }
      return CostContext.ScanBalanceFamily;
    }
    if (isMultiple) return CostContext.ScanBalanceMultiple;
    if (isFollowUp) return CostContext.ScanBalanceFollowUp;
    return CostContext.ScanBalanceInitial;
  };

  const amount = getAmount();

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 340, sm: 420 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          outline: 'none',
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Confirm Scan Payment
        </Typography>

        <Typography sx={{ mb: 3, fontWeight: 500 }}>
          <strong>{getTitle()}</strong>
          {!isFreeScan && amount > 0 && (
            <span> (${formatCentsToDollars(amount)})</span>
          )}
        </Typography>

        {!isFreeScan && amount > 0 && (
          <>
            <Typography sx={{ mt: 2, mb: 3, textAlign: 'right' }}>
              Card: <strong>{cardBrand} x{cardLast4}</strong>
              <br />
              Total Charge: <strong>${formatCentsToDollars(amount)}</strong>
            </Typography>
          </>
        )}

        {isFreeScan && (
          <Typography variant="body1" sx={{ py: 4, textAlign: 'center', fontWeight: 500, color: 'success.main' }}>
            No charge — this is their complimentary onboarding scan.
          </Typography>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={onConfirm}>
            Confirm Payment
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ScanPaymentModal;